import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import fallbackCatalog from "./i18n_catalog.json";
import { publicRequest } from "./apiClient.js";

const SUPPORTED = ["de", "en", "fr", "pt"];
const I18nContext = createContext(null);
let runtimeLanguage = "de";
let runtimeStrings = {};

function normalizeLanguage(value) {
  const code = String(value || "de").slice(0, 2).toLowerCase();
  return SUPPORTED.includes(code) ? code : "de";
}

function fallbackStrings(language) {
  const lang = normalizeLanguage(language);
  return Object.fromEntries(
    Object.entries(fallbackCatalog.strings || {}).map(([key, row]) => [
      key,
      row?.[lang] || row?.de || key,
    ])
  );
}

function formatValue(value, variables = {}) {
  return Object.entries(variables || {}).reduce(
    (result, [name, replacement]) => result.replaceAll(`{${name}}`, String(replacement ?? "")),
    String(value ?? "")
  );
}

export function setRuntimeCatalog(language, strings) {
  runtimeLanguage = normalizeLanguage(language);
  runtimeStrings = { ...fallbackStrings(runtimeLanguage), ...(strings || {}) };
}

export function translateRuntime(key, language = runtimeLanguage, fallback = "", variables = {}) {
  const lang = normalizeLanguage(language);
  const row = fallbackCatalog.strings?.[key];
  const value = lang === runtimeLanguage
    ? runtimeStrings[key]
    : row?.[lang] || row?.de;
  return formatValue(value || fallback || key, variables);
}

function buildLegacyMap(language, strings) {
  const targetLanguage = normalizeLanguage(language);
  const map = new Map();
  for (const [key, row] of Object.entries(fallbackCatalog.strings || {})) {
    const target = strings[key] || row?.[targetLanguage] || row?.de || key;
    for (const sourceLanguage of SUPPORTED) {
      const source = String(row?.[sourceLanguage] || "").trim();
      if (source) map.set(source, target);
    }
  }
  return map;
}

function replaceTextPreservingWhitespace(original, replacement) {
  const leading = original.match(/^\s*/)?.[0] || "";
  const trailing = original.match(/\s*$/)?.[0] || "";
  return `${leading}${replacement}${trailing}`;
}

function translateElementTree(root, legacyMap) {
  if (!root || !legacyMap?.size) return;

  if (root.nodeType === Node.TEXT_NODE) {
    const parent = root.parentElement;
    if (parent?.closest?.("[data-i18n-skip='true']")) return;
    if (["SCRIPT", "STYLE", "CODE", "PRE", "TEXTAREA"].includes(parent?.tagName)) return;
    const source = String(root.nodeValue || "").trim();
    const translated = legacyMap.get(source);
    if (translated && translated !== source) {
      root.nodeValue = replaceTextPreservingWhitespace(root.nodeValue || "", translated);
    }
    return;
  }

  if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) return;
  if (root.nodeType === Node.ELEMENT_NODE) {
    if (root.closest?.("[data-i18n-skip='true']")) return;
    if (["SCRIPT", "STYLE", "CODE", "PRE"].includes(root.tagName)) return;
    for (const attribute of ["placeholder", "title", "aria-label"]) {
      const source = String(root.getAttribute?.(attribute) || "").trim();
      const translated = legacyMap.get(source);
      if (translated && translated !== source) root.setAttribute(attribute, translated);
    }
  }
  Array.from(root.childNodes || []).forEach((child) => translateElementTree(child, legacyMap));
}

export function I18nLegacyBridge() {
  const { language, strings } = useI18n();
  const legacyMap = useMemo(() => buildLegacyMap(language, strings), [language, strings]);

  useEffect(() => {
    const appRoot = document.getElementById("root");
    if (!appRoot) return undefined;
    translateElementTree(appRoot, legacyMap);
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === "characterData") translateElementTree(mutation.target, legacyMap);
        mutation.addedNodes.forEach((node) => translateElementTree(node, legacyMap));
        if (mutation.type === "attributes") translateElementTree(mutation.target, legacyMap);
      }
    });
    observer.observe(appRoot, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ["placeholder", "title", "aria-label"],
    });
    return () => observer.disconnect();
  }, [legacyMap]);

  return null;
}

export function I18nProvider({ children }) {
  const [language, setLanguageState] = useState(() => normalizeLanguage(localStorage.getItem("lenox_language") || "de"));
  const [strings, setStrings] = useState(() => fallbackStrings(language));
  const [ready, setReady] = useState(false);

  const setLanguage = useCallback((value) => {
    const lang = normalizeLanguage(value);
    localStorage.setItem("lenox_language", lang);
    document.documentElement.lang = lang;
    setLanguageState(lang);
  }, []);

  useEffect(() => {
    let alive = true;
    const base = fallbackStrings(language);
    setStrings(base);
    setRuntimeCatalog(language, base);
    document.documentElement.lang = language;
    setReady(false);
    publicRequest(`/i18n/catalog?language=${encodeURIComponent(language)}`)
      .then((response) => {
        if (!alive) return;
        const merged = { ...base, ...(response?.strings || {}) };
        setStrings(merged);
        setRuntimeCatalog(language, merged);
        setReady(true);
      })
      .catch(() => {
        if (!alive) return;
        setRuntimeCatalog(language, base);
        setReady(true);
      });
    return () => { alive = false; };
  }, [language]);

  const t = useCallback(
    (key, variables = {}, fallback = "") => formatValue(strings[key] || fallback || key, variables),
    [strings]
  );

  const value = useMemo(() => ({ language, setLanguage, strings, t, ready }), [language, setLanguage, strings, t, ready]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const value = useContext(I18nContext);
  if (!value) throw new Error("useI18n must be used inside I18nProvider");
  return value;
}

setRuntimeCatalog(runtimeLanguage, fallbackStrings(runtimeLanguage));
